import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { CalendarIcon, MailIcon, ShieldCheckIcon, ShieldIcon } from 'lucide-react';
import { SectionTitle } from '@/components/PageHeader';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ThemeSegmented } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import { Field, FormError } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { api, errorMessage, isApiError } from '@/lib/api';
import { useAuth, useMe } from '@/lib/auth';
import { fmtDate, fmtRelative } from '@/lib/format';
import { initials } from '@/lib/utils';
import { SIDEBAR_DEFAULT, SIDEBAR_MAX, SIDEBAR_MIN, useSidebar } from '@/lib/sidebar';
import { NotificationsSection } from './NotificationsSection';
import { TwoStepSection } from './TwoStepSection';

const profileSchema = z.object({
  firstName: z.string().trim().min(1, 'Required').max(100),
  lastName: z.string().trim().min(1, 'Required').max(100),
  phone: z.string().trim().max(40).optional(),
});
type ProfileValues = z.infer<typeof profileSchema>;

const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Enter your current password'),
    newPassword: z.string().min(10, 'Use at least 10 characters').max(128),
    confirm: z.string(),
  })
  .refine((v) => v.newPassword === v.confirm, { message: 'Passwords do not match', path: ['confirm'] });
type PasswordValues = z.infer<typeof passwordSchema>;

/**
 * Your own account: who you are to the organization at the top (name, level, email, tenure, two-step state), then
 * the things you can change, two cards across on wide screens.
 */
export function AccountPage() {
  const me = useMe();
  const superUser = me.user.tenantRole === 'org_admin';
  return (
    <div className="space-y-6 sm:space-y-8">
      <header className="bg-card border px-4 md:px-6">
        <div className="bg-primary -mx-4 h-0.5 md:-mx-6" aria-hidden />
        <div className="flex items-start gap-3.5 pt-4 pb-4 md:gap-4 md:pt-5 md:pb-5">
          <Avatar className="mt-0.5 size-12 shrink-0">
            <AvatarFallback className="text-base">{initials(me.user.fullName)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <h1 className="font-display min-w-0 truncate text-[2rem] leading-none tracking-tight md:text-[2.5rem]">{me.user.fullName}</h1>
              <div className="flex items-center gap-1.5">
                <span className="text-muted-foreground inline-flex h-6 items-center gap-1.5 border px-2 text-[11px] font-medium whitespace-nowrap">
                  {superUser ? <ShieldCheckIcon className="size-3" aria-hidden /> : <ShieldIcon className="size-3" aria-hidden />} {superUser ? 'Super User' : 'Project Manager'}
                </span>
                <span className="text-muted-foreground inline-flex h-6 items-center gap-1.5 border px-2 text-[11px] font-medium whitespace-nowrap">
                  <span className={me.mfa.enrolled ? 'bg-success size-1.5 rounded-full' : 'bg-muted-foreground/40 size-1.5 rounded-full'} aria-hidden /> Two-step {me.mfa.enrolled ? 'on' : 'off'}
                </span>
              </div>
            </div>
            <div className="text-muted-foreground mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
              <span className="flex min-w-0 items-center gap-1.5">
                <MailIcon className="size-3.5 shrink-0" aria-hidden />
                <span className="truncate">{me.user.email}</span>
              </span>
              <span className="flex items-center gap-1.5 whitespace-nowrap">
                <CalendarIcon className="size-3.5" aria-hidden /> Since {fmtDate(me.user.createdAt)}
              </span>
              {me.user.lastLoginAt ? <span className="whitespace-nowrap">Last sign-in {fmtRelative(me.user.lastLoginAt)}</span> : null}
            </div>
          </div>
        </div>
      </header>
      <div className="grid gap-6 lg:grid-cols-2 lg:items-start lg:gap-8">
        <ProfileSection />
        <PasswordSection />
        <TwoStepSection />
        <NotificationsSection />
        <section className="bg-card border p-6">
          <SectionTitle className="mb-2">Appearance</SectionTitle>
          <p className="text-muted-foreground mb-4 text-sm">Theme preference is saved on this device.</p>
          <ThemeSegmented />
        </section>
        <SidebarSection />
      </div>
    </div>
  );
}

function ProfileSection() {
  const me = useMe();
  const { refreshMe } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<ProfileValues>({ resolver: zodResolver(profileSchema), defaultValues: { firstName: me.user.firstName, lastName: me.user.lastName, phone: me.user.phoneDisplay ?? me.user.phone ?? '' } });

  const onSubmit = form.handleSubmit(async (v) => {
    setError(null);
    try {
      await api.auth.updateProfile({ firstName: v.firstName, lastName: v.lastName, phone: v.phone?.trim() ? v.phone.trim() : null });
      await refreshMe();
      form.reset(v);
      toast.success('Profile updated');
    } catch (err) {
      setError(errorMessage(err));
    }
  });

  return (
    <section className="bg-card border p-6">
      <SectionTitle className="mb-5">Profile</SectionTitle>
      <form onSubmit={onSubmit} className="grid gap-4" noValidate>
        <FormError message={error} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name" htmlFor="p-first" error={form.formState.errors.firstName?.message}>
            <Input id="p-first" autoComplete="given-name" {...form.register('firstName')} />
          </Field>
          <Field label="Last name" htmlFor="p-last" error={form.formState.errors.lastName?.message}>
            <Input id="p-last" autoComplete="family-name" {...form.register('lastName')} />
          </Field>
        </div>
        <Field label="Mobile number" htmlFor="p-phone" description="Optional. Used to text you a one-time sign-in code when your organization allows it." error={form.formState.errors.phone?.message}>
          <Input id="p-phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="(407) 555-0123" {...form.register('phone')} />
        </Field>
        <Field label="Email" htmlFor="p-email" description="Contact your organization admin to change your email.">
          <Input id="p-email" value={me.user.email} disabled readOnly />
        </Field>
        <div className="flex justify-end">
          <Button type="submit" disabled={form.formState.isSubmitting || !form.formState.isDirty}>
            {form.formState.isSubmitting ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </form>
    </section>
  );
}

function PasswordSection() {
  const [error, setError] = useState<string | null>(null);
  const form = useForm<PasswordValues>({ resolver: zodResolver(passwordSchema), defaultValues: { currentPassword: '', newPassword: '', confirm: '' } });

  const onSubmit = form.handleSubmit(async (v) => {
    setError(null);
    try {
      await api.auth.changePassword(v.currentPassword, v.newPassword);
      form.reset();
      toast.success('Password changed');
    } catch (err) {
      if (isApiError(err) && err.status === 422) {
        const fe = err.fieldErrors();
        if (fe['newPassword']) form.setError('newPassword', { message: fe['newPassword'] });
      }
      setError(errorMessage(err));
    }
  });

  return (
    <section className="bg-card border p-6">
      <SectionTitle className="mb-5">Password</SectionTitle>
      <form onSubmit={onSubmit} className="grid gap-4" noValidate>
        <FormError message={error} />
        <Field label="Current password" htmlFor="pw-current" error={form.formState.errors.currentPassword?.message}>
          <Input id="pw-current" type="password" autoComplete="current-password" {...form.register('currentPassword')} />
        </Field>
        <Field label="New password" htmlFor="pw-new" description="At least 10 characters." error={form.formState.errors.newPassword?.message}>
          <Input id="pw-new" type="password" autoComplete="new-password" {...form.register('newPassword')} />
        </Field>
        <Field label="Confirm new password" htmlFor="pw-confirm" error={form.formState.errors.confirm?.message}>
          <Input id="pw-confirm" type="password" autoComplete="new-password" {...form.register('confirm')} />
        </Field>
        <div className="flex justify-end">
          <Button type="submit" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? 'Changing…' : 'Change password'}
          </Button>
        </div>
      </form>
    </section>
  );
}

function SidebarSection() {
  const sidebar = useSidebar();
  return (
    <section className="bg-card border p-6">
      <SectionTitle className="mb-2">Sidebar</SectionTitle>
      <p className="text-muted-foreground mb-4 text-sm">Saved on this device. You can also drag the sidebar’s edge, or press [ to collapse it.</p>
      <div className="grid gap-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label htmlFor="sidebar-collapsed" className="text-sm font-medium">
              Collapsed
            </Label>
            <p className="text-muted-foreground text-xs">Icons only, with the labels on hover.</p>
          </div>
          <Switch id="sidebar-collapsed" checked={sidebar.collapsed} onCheckedChange={(v) => sidebar.setCollapsed(v)} />
        </div>
        <div className="grid gap-2">
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor="sidebar-width" className="text-sm font-medium">
              Width
            </Label>
            <span className="text-muted-foreground text-xs tabular-nums">
              {sidebar.width}px{sidebar.width !== SIDEBAR_DEFAULT ? ` · default ${SIDEBAR_DEFAULT}px` : ''}
            </span>
          </div>
          <input id="sidebar-width" type="range" min={SIDEBAR_MIN} max={SIDEBAR_MAX} step={4} value={sidebar.width} onChange={(e) => sidebar.setWidth(Number(e.target.value))} className="accent-primary w-full" aria-valuetext={`${sidebar.width} pixels`} />
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={sidebar.reset} disabled={sidebar.width === SIDEBAR_DEFAULT && !sidebar.collapsed}>
              Reset to default
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
